import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { pb } from '../../lib/pb';

interface ActorExpand {
  id: string;
  name: string;
  email: string;
  unit_number: string | null;
}

interface AuditRow {
  id: string;
  actor: string;
  action: string;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown> | null;
  ip_addr: string;
  user_agent: string;
  created: string;
  expand?: { actor?: ActorExpand };
}

const ACTION_LABELS: Record<string, string> = {
  'user.login': 'Sign-in',
  'user.role_changed': 'Role changed',
  'user.password_reset_link_sent': 'Password reset link sent',
  'user.email_changed': 'Email address changed',
  'user.name_changed': 'Display name changed',
  'document.created': 'Document uploaded',
  'document.deleted': 'Document deleted',
  'document.approval_changed': 'Minutes approval changed',
  'records_request.opened': 'Records request opened',
  'records_request.resolved': 'Records request resolved',
};

type RangeKey = '7d' | '30d' | '90d' | 'all';

const RANGE_OPTIONS: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

function formatDateTime(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function actorLabel(row: AuditRow): string {
  const a = row.expand?.actor;
  if (a) {
    return a.unit_number ? `${a.name} · Unit ${a.unit_number}` : a.name;
  }
  if (!row.actor) return '(system)';
  return `(deleted user ${row.actor.slice(0, 8)})`;
}

function targetLabel(row: AuditRow): string {
  if (!row.target_type) return '';
  const meta = row.metadata || {};
  if (row.target_type === 'user') {
    const email = typeof meta.target_email === 'string' ? meta.target_email : '';
    return email ? `${email}` : `user ${row.target_id.slice(0, 8)}`;
  }
  if (row.target_type === 'document') {
    const title = typeof meta.title === 'string' ? meta.title : '';
    return title || `document ${row.target_id.slice(0, 8)}`;
  }
  if (row.target_type === 'records_request') {
    return `request ${row.target_id.slice(0, 8)}`;
  }
  return `${row.target_type} ${row.target_id.slice(0, 8)}`;
}

function metadataSummary(row: AuditRow): string {
  const m = row.metadata || {};
  if (row.action === 'user.role_changed') {
    return `${m.from_role ?? '?'} → ${m.to_role ?? '?'}`;
  }
  if (row.action === 'records_request.resolved') {
    return `${m.from_status ?? '?'} → ${m.to_status ?? '?'}`;
  }
  if (row.action === 'records_request.opened') {
    const cats = Array.isArray(m.categories) ? (m.categories as string[]).join(', ') : '';
    return cats || '';
  }
  if (row.action === 'document.created' || row.action === 'document.deleted') {
    const cat = typeof m.category === 'string' ? m.category : '';
    const tier = m.is_admin_only ? 'admin-only' : m.is_owner_only ? 'owner-only' : 'public';
    return [cat, tier].filter(Boolean).join(' · ');
  }
  if (row.action === 'document.approval_changed') {
    const from = m.from_approved ? 'approved' : 'unapproved';
    const to = m.to_approved ? 'approved' : 'unapproved';
    return `${from} → ${to}`;
  }
  if (row.action === 'user.email_changed') {
    return `${m.from_email ?? '?'} → ${m.to_email ?? '?'}`;
  }
  if (row.action === 'user.name_changed') {
    return `"${m.from_name ?? ''}" → "${m.to_name ?? ''}"`;
  }
  if (row.action === 'user.login') {
    return typeof m.role === 'string' ? m.role : '';
  }
  return '';
}

function rowsToCsv(rows: AuditRow[]): string {
  const header = [
    'created',
    'action',
    'actor_id',
    'actor_email',
    'target_type',
    'target_id',
    'metadata',
    'ip_addr',
    'user_agent',
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    const a = r.expand?.actor;
    const cells = [
      r.created,
      r.action,
      r.actor || '',
      a?.email || '',
      r.target_type || '',
      r.target_id || '',
      JSON.stringify(r.metadata || {}),
      r.ip_addr || '',
      r.user_agent || '',
    ];
    lines.push(
      cells
        .map((c) => {
          const s = String(c);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(','),
    );
  }
  return lines.join('\n');
}

interface ActorOption {
  id: string;
  label: string;
}

export function AdminAuditLog(): JSX.Element {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [actorFilter, setActorFilter] = useState<string>('');
  const [range, setRange] = useState<RangeKey>('30d');
  const [actors, setActors] = useState<ActorOption[]>([]);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Admin · Audit Log | Sunset Harbor';
    return () => {
      document.title = previous;
    };
  }, []);

  // Load the actor dropdown options once. Pull users sorted by name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await pb.collection('users').getList<ActorExpand>(1, 200, {
          sort: 'name',
          fields: 'id,name,email,unit_number',
        });
        if (cancelled) return;
        setActors(
          result.items.map((u) => ({
            id: u.id,
            label: u.unit_number ? `${u.name} · Unit ${u.unit_number}` : u.name || u.email,
          })),
        );
      } catch (err) {
        console.error('Could not load actors list', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filter = useMemo(() => {
    const clauses: string[] = [];
    if (actionFilter) clauses.push(`action = "${actionFilter}"`);
    if (actorFilter) clauses.push(`actor = "${actorFilter}"`);
    const days = RANGE_OPTIONS.find((r) => r.key === range)?.days;
    if (days) {
      const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      clauses.push(`created >= "${cutoff}"`);
    }
    return clauses.join(' && ');
  }, [actionFilter, actorFilter, range]);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const result = await pb.collection('audit_log').getList<AuditRow>(1, 500, {
        filter,
        sort: '-created',
        expand: 'actor',
      });
      setRows(result.items);
    } catch (err) {
      console.error(err);
      setError('Could not load audit log entries.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  function onExport(): void {
    if (!rows || rows.length === 0) return;
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `sunset-harbor-audit-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHero
        eyebrow="Admin"
        title="Audit log"
        lead="Append-only record of sign-ins, role changes, document uploads and deletions, and records-request lifecycle events. Read-only. Entries cannot be edited or removed."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to portal
            </Link>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => void load()}
                disabled={loading}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-brand-surface bg-brand-white px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface/40 disabled:opacity-60"
              >
                <RefreshCw size={14} aria-hidden="true" className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                type="button"
                onClick={onExport}
                disabled={!rows || rows.length === 0}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-yellow px-4 text-sm font-semibold text-brand-charcoal hover:bg-brand-yellow-deep disabled:opacity-60"
              >
                <Download size={14} aria-hidden="true" />
                Export CSV
              </button>
            </div>
          </div>

          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                Action
              </span>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-sm text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              >
                <option value="">All actions</option>
                {Object.entries(ACTION_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                Actor
              </span>
              <select
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-sm text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              >
                <option value="">All actors</option>
                {actors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                Time range
              </span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value as RangeKey)}
                className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-sm text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              >
                {RANGE_OPTIONS.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error ? (
            <div
              role="alert"
              className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : null}

          {rows === null ? (
            <p className="text-sm text-brand-body-medium">Loading...</p>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-brand-surface bg-brand-surface/30 px-6 py-12 text-center">
              <p className="text-sm text-brand-body-medium">
                No audit entries match the current filter.
              </p>
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs text-brand-body-medium">
                {rows.length} {rows.length === 1 ? 'entry' : 'entries'} (most recent first)
              </p>
              <div className="overflow-x-auto rounded-lg border border-brand-surface">
                <table className="w-full min-w-[800px] border-collapse text-sm">
                  <thead className="bg-brand-surface/40 text-left text-xs font-semibold uppercase tracking-[0.12em] text-brand-body-medium">
                    <tr>
                      <th className="px-4 py-3">Time</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">Target</th>
                      <th className="px-4 py-3">Details</th>
                      <th className="px-4 py-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-brand-surface align-top">
                        <td className="whitespace-nowrap px-4 py-3 text-brand-body-medium">
                          {formatDateTime(r.created)}
                        </td>
                        <td className="px-4 py-3 text-brand-charcoal">{actorLabel(r)}</td>
                        <td className="px-4 py-3 font-semibold text-brand-charcoal">
                          {ACTION_LABELS[r.action] ?? r.action}
                        </td>
                        <td className="px-4 py-3 text-brand-body-dark">{targetLabel(r)}</td>
                        <td className="px-4 py-3 text-brand-body-medium">{metadataSummary(r)}</td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-brand-body-medium">
                          {r.ip_addr || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Container>
      </section>
    </>
  );
}
